import { LocalDictionaryFactory } from '../../api/api.module'

import { GridBridge } from '../../common/common.interfaces'
import { ColumnModel } from '../../api/api.models';
import { Activity, Enums } from '../../../models/common'
import { OpTariff, OpDataTypeInGroup, OpLocation } from '../../../models/core'

import { ConfigHelper, EventsNames, LocalizationHelper, LogHelper, OperatorLayoutSettings, TemplateHelper, TypeHelper } from '../../../helpers'
import { TariffService } from '../../../services/tariff'
import { TariffLocationsService } from '../../../services/tariff'
import { GridService } from '../../../services/grid'
import { UserSessionService } from '../../../services/user-session'

import { CreateEditInitParams } from '../../common/create-edit-popup/create-edit-popup.controller'
import { Details, DetailsScope, DetailsAccordion, DetailsAccordionSection } from '../../common/common.details'
import { DetailsParametersOptions } from '../../common/details-parameters/details-parameters.directive'
import { DetailsPopupOptions, DetailsOptions } from '../../common/details-popup/details-popup.directive'
import { GridOptions, GridTypes } from '../../common/grid/grid.controller'
import { LoggerService } from '../../common/common.module'

interface TariffDetailsScope extends DetailsScope {
    id: number

    deleteConfirmationOptions: {
        title: string
        onConfirmed: () => void
    }
}

export class TariffDetailsDirectiveController extends Details<TariffDetailsScope> {

    // #region Const

    private TARIFF_COMMON_TAB_NAME: string = 'common'

    private TARIFF_PARAMS_ACCORDION_NAME: string = 'TARIFF_PARAMS_ACCORDION_NAME'
    private TARIFF_LOCATIONS_ACCORDION_NAME: string = 'TARIFF_LOCATIONS_ACCORDION_NAME'

    private TARIFF_LOCATIONS_GRID_NAME: string = 'tariffLocationsGrid'
    private TARIFF_LOCATIONS_GRID_EDIT_NAME: string = 'tariffLocationsGridEdit'

    private TARIFF_LOCATION: string = 'location'

    // #endregion


    // #region Members

    private referenceObject: OpTariff
    private referenceObjectId: number

    private detailsParametersOptions: DetailsParametersOptions
    private tariffLocations: OpLocation[]

    private tariffLocationsGridOptions: GridOptions
    private tariffLocationsGridEditOptions: GridBridge

    private noActiveAccordions: string
    private noDefinedContent: string

    // #endregion


    // #region Ctor

    constructor(
        $scope: TariffDetailsScope,
        $q: ng.IQService,
        $timeout: ng.ITimeoutService,
        logger: LoggerService,
        localDictionary: LocalDictionaryFactory,
        protected userSessionService: UserSessionService,
        private gridService: GridService,
        private tariffService: TariffService,
        private tariffLocationsService: TariffLocationsService,
        protected referenceType: Enums.ReferenceType,
        private moduleName: Enums.SimaxModuleName
    ) {
        super($scope, $timeout, $q, logger, localDictionary, OperatorLayoutSettings.ContactPopupTabActive, userSessionService, referenceType)

        this.setConfirmationsOptions()
        this.setEvents()
        this.setOptions()
        this.setTemplateConsts()

        if (this.userSession.getLoggedOperator().hasPermission(Activity.TARIFF_LIST)) {
            this.configureTabs(this.getTabs())
        }
    }

    // #endregion


    // #region Events

    private onOpen(options: DetailsOptions): void {
        this.$scope.detailsOpenParams = options

        this.referenceObjectId = options.referenceObjectId

        this.onClear()
        this.showPopup()
        this.onRefresh()
    }

    private onRefresh(toRefresh: OpTariff = null): void {
        this.$scope.options.isDataLoaded = false

        if (toRefresh == null) {
            this.tariffService
                .getTariff(this.referenceObjectId)
                .then((tariff: OpTariff) => {
                    return $.when(this.initialize(tariff))
                })
                .fail(() => {
                    this.hidePopup()
                })
                .always(() => {
                    this.$scope.options.isDataLoaded = true
                })
        } else {
            $.when(
                this.initialize(toRefresh)
            ).always(() => {
                this.$scope.options.isDataLoaded = true
            })
        }
    }

    private onEditTariffLocations(): void {
        this.$scope.$broadcast(EventsNames.OPEN_GRID_EDIT_POPUP + this.$scope.detailsPopupId + this.TARIFF_LOCATION)
    }

    private onClear(): void {
        this.$scope.popupTitle = TypeHelper.emptyString
        this.$scope.showTabs = false

        this.referenceObject = null
        this.detailsParametersOptions = null
        this.tariffLocations = null
    }

    private onClose(): void {
        this.hidePopup()
    }

    private onConfirmDelete(): void {
        this.tariffService
            .deleteTariff(this.referenceObjectId, this.moduleName)
            .then(() => {
                this.$scope.$root.$broadcast(EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__DELETED, this.moduleName))
                this.onClose()
            })
            .fail((error: any) => {
                LogHelper.error(TariffService.LOG_PREFIX, error)
            })
    }

    private onDelete(): void {
        this.$scope.$broadcast(
            EventsNames.OPEN_CONFIRMATION_POPUP + this.$scope.detailsPopupId,
            LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__CONFIRM_DELETE, this.moduleName)
        )
    }

    private onEditTariffParams(): void {
        const createEditInitParams: CreateEditInitParams = this.tariffService.getCreateEditOptions(this.referenceObject.idTariff)
        this.$scope.$root.$broadcast(EventsNames.INITIALIZE_CREATE_EDIT_POPUP, createEditInitParams)
    }

    private onSaveTariffLocation(added: any[], removed: any[]): ng.IPromise<boolean> {
        const deferred = this.$q.defer<boolean>()

        this.tariffLocationsService
            .saveTariffLocations(this.moduleName, this.referenceObject, added, removed)
            .then((savedObject: OpTariff) => {
                this.onRefresh(savedObject)

                deferred.resolve(true)
            })
            .fail((error: any) => {
                LogHelper.error(TariffService.LOG_PREFIX, error)

                deferred.resolve(false)
            })

        return deferred.promise
    }

    // #endregion


    //#region Initialize

    private initialize(tariff: OpTariff): JQueryPromise<void> {
        const def: JQueryDeferred<void> = $.Deferred<void>()

        if (tariff != null && (this.referenceObject == null || this.referenceObject.idTariff == tariff.idTariff)) {
            this.referenceObject = tariff
            this.$scope.popupTitle = this.referenceObject.toString()
            this.tariffLocations = this.referenceObject.currentLocations

            this.$scope.showTabs = true

            $.when(
                this.setBasicParameters(),
                this.setTariffLocationsParameters()
            ).always(() => {
                def.resolve()
            })
        } else {
            def.resolve()
        }

        return def.promise()
    }

    //#endregion


    // #region Setup

    private setBasicParameters(): JQueryPromise<void> {
        const def: JQueryDeferred<void> = $.Deferred<void>()

        this.tariffService.getBasicParametersDataTypeGroup()
            .then((dataTypeInGroup: OpDataTypeInGroup[]) => {
                this.detailsParametersOptions = {
                    dataTypes: dataTypeInGroup,
                    referenceType: this.referenceType,
                    referenceObject: this.referenceObject,
                    onDataLoaded: () => {
                        def.resolve()
                    }
                }
            })
            .catch(() => {
                def.reject()
            })

        if (this.isAccordionHidden(this.TARIFF_PARAMS_ACCORDION_NAME)) {
            def.resolve()
        }

        return def.promise()
    }

    private setTariffLocationsParameters(): JQueryPromise<void> {
        return this.tariffLocationsService.getTariffLocationsParameters()
            .then((columns: ColumnModel[]) => {
                this.setTariffLocationsGridEditOptions(this.referenceObject.currentLocations, columns)
                this.setTariffLocationsGridOptions(this.referenceObject.currentLocations, columns)
            })
    }

    public getAccordions(): DetailsAccordion[] {
        const accordions: DetailsAccordion[] = []
        let items: DetailsAccordionSection[] = []

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.TARIFF_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.TariffParameters)) {
            items = []

            items.push({
                name: this.TARIFF_PARAMS_ACCORDION_NAME,
                showInTab: this.TARIFF_COMMON_TAB_NAME,
                showStatus: true,
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.BasicParameters),
                visible: true,
                canEdit: this.userSession.getLoggedOperator().hasPermission(Activity.TARIFF_EDIT)
                    && !this.userSession.getLoggedOperator().hasPermission(Activity.TARIFF_DETAILS_READONLY_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.TariffParameters),
                editMethod: () => this.onEditTariffParams(),
            })

            accordions.push(new DetailsAccordion(this.$scope, this.$timeout, OperatorLayoutSettings.ContactDetailsAccordions, 0, items))
        }

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.TARIFF_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.TariffLocations)) {
            items = []

            items.push({
                name: this.TARIFF_LOCATIONS_ACCORDION_NAME,
                showInTab: this.TARIFF_COMMON_TAB_NAME,
                showStatus: true,
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Locations),
                visible: true,
                canEdit: this.userSession.getLoggedOperator().hasPermission(Activity.TARIFF_EDIT)
                    && !this.userSession.getLoggedOperator().hasPermission(Activity.TARIFF_DETAILS_READONLY_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.TariffLocations),
                editMethod: () => this.onEditTariffLocations(),
                status: () => LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AssignedCount) + (this.tariffLocations ? this.tariffLocations.length : 0),
            })

            accordions.push(new DetailsAccordion(this.$scope, this.$timeout, OperatorLayoutSettings.ContactDetailsAccordions, 1, items))
        }


        return accordions
    }

    public getTabs(): any[] {
        const tabs: any[] = []

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.TARIFF_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.TariffParameters)
            || !this.userSession.getLoggedOperator().hasPermission(Activity.TARIFF_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.TariffLocations)) {
            tabs.push({
                template: TemplateHelper.getContextMenuItem(
                    LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Common),
                    ConfigHelper.Icon.HOME,
                    this.userSession
                ),
                name: this.TARIFF_COMMON_TAB_NAME
            })
        }

        return tabs
    }

    private setTariffLocationsGridEditOptions(locations: OpLocation[], columns: ColumnModel[]): void {
        this.tariffLocationsGridEditOptions = {
            columns,
            id: this.referenceObjectId,
            items: locations.map((location: OpLocation) => this.gridService.gridObjectFromIOpObject(location)),
            name: this.TARIFF_LOCATIONS_GRID_EDIT_NAME,
            popupId: this.$scope.detailsPopupId + this.TARIFF_LOCATION,
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__LOCATIONS_EDIT, this.moduleName),
            allowColumnReordering: !this.userSession
                .getLoggedOperator()
                .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.TariffLocations),
            getAvailableDataSourceMethod: this.userSession
                .getDataProvider()
                .Location
                .getLocationFilter,
            onSubmit: (added: any[], removed: any[]) => this.onSaveTariffLocation(added, removed)
        }
    }

    private setTariffLocationsGridOptions(locations: OpLocation[], columns: ColumnModel[]): void {
        this.tariffLocationsGridOptions = null

        this.$timeout(() => {
            this.tariffLocationsGridOptions = {
                name: this.TARIFF_LOCATIONS_GRID_NAME,
                idReferenceType: Enums.ReferenceType.IdLocation,
                type: GridTypes.PAGINATED_SMALL,
                buttonsOptions: {
                    enabled: true,
                },
                addCustomFilters: true,
                dataSource: locations,
                columnsDictionary: columns,
                allowColumnReordering: !this.userSession.getLoggedOperator().hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.TariffLocations)
            }
        }, 100)
    }

    private setConfirmationsOptions(): void {
        this.$scope.deleteConfirmationOptions = {
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETING, this.moduleName),
            onConfirmed: () => this.onConfirmDelete()
        }
    }

    private setEvents(): void {
        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__OPEN_DETAILS_POPUP, this.moduleName) + this.$scope.id,
            (event: ng.IAngularEvent, ...args: any[]) => this.onOpen(args[3])
        )

        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__CLOSE_DETAILS_POPUP, this.moduleName) + this.$scope.id,
            () => this.onClose()
        )

        this.$scope.$on(EventsNames.CLOSE_CREATE_EDIT_POPUP, (event: ng.IAngularEvent, ...args: any[]) => this.onRefresh(args[0]))
    }

    private setOptions(): void {
        this.$scope.options = <DetailsPopupOptions>{
            id: this.$scope.id,
            closeEventName: EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__CLOSE_DETAILS_POPUP, this.moduleName),
            delHasPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.TARIFF_DEL) ? Activity[Activity.TARIFF_DEL] : null,
            openEventName: EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__OPEN_DETAILS_POPUP, this.moduleName),
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DETAILS, this.moduleName),
            deleteMethod: () => this.onDelete(),
            getAccordionDataMethod: () => this.getAccordionData(),
            getAccordionSelectionMethod: () => this.getAccordionSelection(),
            updateAccordionMethod: (names: any[]) => this.updateAccordions(names)
        }
    }

    private setTemplateConsts(): void {
        this.noActiveAccordions = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NoActiveAccordions)
        this.noDefinedContent = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NoDefinedContent)
    }


    // #endregion

    resetData() { this.onClear() }//DO USUNIECIA!!! - zastąpić onClear
    loadData(id: number, objects: any[]) { this.onOpen(null) }//DO USUNIECIA!!! - zastąpić onOpen
    updateData(result: any) { this.onRefresh(result) }//DO USUNIECIA!!! - zastąpić onRefresh
}